/**
 * Admin Layout
 * Navy sidebar (#00263E) + white header for Dev Admin panel
 */
import { Layout, Menu, Space, Tag } from 'antd'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useSnapshot } from 'valtio'
import { useTranslation } from 'react-i18next'
import {
  DashboardOutlined,
  DeploymentUnitOutlined,
  HeartOutlined,
  ApartmentOutlined,
  PlayCircleOutlined,
  LineChartOutlined,
  ApiOutlined,
  TeamOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  CloudServerOutlined,
  AuditOutlined,
  EyeOutlined,
  ExperimentOutlined,
  LeftOutlined,
  SwapOutlined,
  ExportOutlined,
} from '@ant-design/icons'
import { uiStore, toggleSidebar } from '@store/uiStore'
import { useTenants } from '@queries/tenant'
import { ROUTES } from '@shared/constants'
import { UserMenu } from '@presentation/components'
import { auxaliaColors } from '@shared/theme'
import { AdminSidebarFooter } from './AdminSidebarFooter'
import { VersionMismatchToast } from '@presentation/components/VersionMismatchToast'

const { Header, Sider, Content } = Layout

export function AdminLayout() {
  const { sidebarCollapsed } = useSnapshot(uiStore)
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  // Pre-fetch tenant list for tenant name resolution across all pages
  useTenants()

  const menuItems = [
    {
      key: ROUTES.DASHBOARD,
      icon: <DashboardOutlined />,
      label: !sidebarCollapsed ? t('navigation.overview') : '',
      title: sidebarCollapsed ? t('navigation.overview') : undefined,
    },
    {
      key: ROUTES.HEALTH,
      icon: <HeartOutlined />,
      label: !sidebarCollapsed ? t('navigation.health') : '',
      title: sidebarCollapsed ? t('navigation.health') : undefined,
    },
    {
      key: ROUTES.WORKFLOWS,
      icon: <ApartmentOutlined />,
      label: !sidebarCollapsed ? t('navigation.workflows') : '',
      title: sidebarCollapsed ? t('navigation.workflows') : undefined,
    },
    {
      key: ROUTES.EXECUTIONS,
      icon: <PlayCircleOutlined />,
      label: !sidebarCollapsed ? t('navigation.executions') : '',
      title: sidebarCollapsed ? t('navigation.executions') : undefined,
    },
    {
      key: ROUTES.MONITORING,
      icon: <LineChartOutlined />,
      label: !sidebarCollapsed ? t('navigation.monitoring') : '',
      title: sidebarCollapsed ? t('navigation.monitoring') : undefined,
    },
    {
      key: ROUTES.DEPLOYMENT,
      icon: <DeploymentUnitOutlined />,
      label: !sidebarCollapsed ? t('navigation.deployment') : '',
      title: sidebarCollapsed ? t('navigation.deployment') : undefined,
    },
    {
      key: ROUTES.LIVE_TESTS,
      icon: <ExperimentOutlined />,
      label: !sidebarCollapsed ? t('navigation.liveTests') : '',
      title: sidebarCollapsed ? t('navigation.liveTests') : undefined,
    },
    {
      key: ROUTES.CONNECTORS,
      icon: <ApiOutlined />,
      label: !sidebarCollapsed ? t('navigation.connectors') : '',
      title: sidebarCollapsed ? t('navigation.connectors') : undefined,
    },
    {
      key: ROUTES.TENANTS,
      icon: <BankOutlined />,
      label: !sidebarCollapsed ? t('navigation.tenants') : '',
      title: sidebarCollapsed ? t('navigation.tenants') : undefined,
    },
    {
      key: ROUTES.IDENTITY_SWITCH_OVER,
      icon: <SwapOutlined />,
      label: !sidebarCollapsed ? t('navigation.identitySwitchOver') : '',
      title: sidebarCollapsed ? t('navigation.identitySwitchOver') : undefined,
    },
    {
      key: ROUTES.PERSON_EXPORT,
      icon: <ExportOutlined />,
      label: !sidebarCollapsed ? t('navigation.personExport') : '',
      title: sidebarCollapsed ? t('navigation.personExport') : undefined,
    },
    {
      key: ROUTES.GROUPS,
      icon: <TeamOutlined />,
      label: !sidebarCollapsed ? t('navigation.groups') : '',
      title: sidebarCollapsed ? t('navigation.groups') : undefined,
    },
    {
      key: ROUTES.USERS,
      icon: <UserOutlined />,
      label: !sidebarCollapsed ? t('navigation.users') : '',
      title: sidebarCollapsed ? t('navigation.users') : undefined,
    },
    {
      key: ROUTES.TEMPLATES,
      icon: <FileTextOutlined />,
      label: !sidebarCollapsed ? t('navigation.templates') : '',
      title: sidebarCollapsed ? t('navigation.templates') : undefined,
    },
    {
      key: ROUTES.AUDIT,
      icon: <AuditOutlined />,
      label: !sidebarCollapsed ? t('navigation.audit') : '',
      title: sidebarCollapsed ? t('navigation.audit') : undefined,
    },
    {
      key: ROUTES.PRESENTATION,
      icon: <EyeOutlined />,
      label: !sidebarCollapsed ? t('navigation.presentation') : '',
      title: sidebarCollapsed ? t('navigation.presentation') : undefined,
    },
    { type: 'divider' as const },
    {
      key: 'infra-group',
      icon: <CloudServerOutlined />,
      label: t('navigation.infrastructure'),
      children: [
        { key: ROUTES.INFRA, label: t('navigation.infraOverview') },
        { key: ROUTES.INFRA_REDIS, label: 'Redis' },
        { key: ROUTES.INFRA_SERVICE_BUS, label: 'Service Bus' },
        { key: '/infra/db/identity', label: 'DB: Identity' },
        { key: '/infra/db/workflows', label: 'DB: Workflows' },
        { key: '/infra/db/connectors', label: 'DB: Connectors' },
        { key: '/infra/db/triggers', label: 'DB: Triggers' },
        { key: '/infra/db/notifications', label: 'DB: Notifications' },
        { key: '/infra/db/orchestration', label: 'DB: Orchestration' },
        { key: '/infra/db/infrastructure', label: 'DB: Infrastructure' },
      ],
    },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key.startsWith('/')) {
      navigate(key)
    }
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible={false}
        collapsed={sidebarCollapsed}
        theme="dark"
        width={240}
        collapsedWidth={64}
        style={{
          overflow: 'hidden',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: auxaliaColors.navy,
          borderRight: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        {/* Flex column: logo + scrollable menu + pinned footer */}
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Logo Section */}
          <div
            style={{
              padding: sidebarCollapsed ? '16px 8px' : '16px',
              borderBottom: '1px solid rgba(113, 148, 165, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              minHeight: 64,
              gap: 8,
              flexShrink: 0,
            }}
          >
            {sidebarCollapsed ? (
              <Tag color={auxaliaColors.lime} style={{ margin: 0, color: auxaliaColors.navy, fontWeight: 700 }}>
                DEV
              </Tag>
            ) : (
              <>
                <span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>AX Flow</span>
                <Tag color={auxaliaColors.lime} style={{ margin: 0, color: auxaliaColors.navy, fontWeight: 700 }}>
                  DEV
                </Tag>
              </>
            )}
          </div>

          {/* Navigation Menu — scrolls when items overflow */}
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            <Menu
              mode="inline"
              theme="dark"
              inlineCollapsed={sidebarCollapsed}
              selectedKeys={[location.pathname.startsWith(ROUTES.GROUPS) ? ROUTES.GROUPS : location.pathname]}
              defaultOpenKeys={location.pathname.startsWith('/infra') ? ['infra-group'] : []}
              items={menuItems}
              onClick={handleMenuClick}
              style={{
                borderRight: 0,
                marginTop: 8,
                background: 'transparent',
              }}
            />
          </div>

          {/* Version footer — pinned to bottom */}
          <AdminSidebarFooter collapsed={sidebarCollapsed} />
        </div>

        {/* Collapse Button */}
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={toggleSidebar}
        >
          <LeftOutlined
            style={{
              fontSize: 12,
              color: '#7194a5',
              transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </div>
      </Sider>

      <Layout
        style={{
          marginLeft: sidebarCollapsed ? 64 : 240,
          transition: 'all 0.2s',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
            height: 64,
            flexShrink: 0,
            zIndex: 100,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, color: auxaliaColors.navy }}>
            Dev Admin Panel
          </span>

          <Space size="large">
            <UserMenu />
          </Space>
        </Header>

        <Content
          style={{
            margin: 0,
            padding: 24,
            background: '#f6f8fa',
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
          }}
        >
          <Outlet />
        </Content>
        <VersionMismatchToast />
      </Layout>
    </Layout>
  )
}

export default AdminLayout
